import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePremium } from '../contexts/PremiumContext';
import { useTranslation } from '../hooks/useTranslation';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDoc, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { FRIENDSHIP_TYPES, getRelationshipIcon, getFriendshipTypeName } from '../utils/friendshipTypes';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import MilestoneTracker from './MilestoneTracker';
import CadenceTracker from './CadenceTracker';
import ProgressIndicator from './ProgressIndicator';
import ReflectionPrompt from './ReflectionPrompt';
import ScheduleAppointmentModal from './ScheduleAppointmentModal';
import { getDaysSinceLastContact, getContactStatus } from '../utils/contactStatus';
import { createDeepeningJourney } from '../utils/friendJourneys';

const FriendshipTracker = ({ focusFriendId = null, onStartJourney = null, showFriendDetailsOnLoad = false, onFriendDetailsClosed = null, onFriendClick = null, showFriendshipTypeOnLoad = false, selectedFriendForType = null, onFriendshipTypeClosed = null, onBack = null, initialActiveTab = 'friends' }) => {
  const { currentUser } = useAuth();
  const { canAddFriend, isPremium, isFriendAccessible } = usePremium();
  const { t } = useTranslation();
  const [friends, setFriends] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [followUpReminders, setFollowUpReminders] = useState([]);
  const [contactHistory, setContactHistory] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [selectedReflection, setSelectedReflection] = useState(null);
  const [showReflectionView, setShowReflectionView] = useState(false);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [showScheduleAppointment, setShowScheduleAppointment] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [activeTab, setActiveTab] = useState('friends'); // 'friends', 'appointments', 'history'
  const [editingFriendId, setEditingFriendId] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [selectedFriendForJourney, setSelectedFriendForJourney] = useState(null);
  const [showFriendDetails, setShowFriendDetails] = useState(false);
  const [selectedFriendForDetails, setSelectedFriendForDetails] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedFriendForContact, setSelectedFriendForContact] = useState(null);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [selectedFriendForGoals, setSelectedFriendForGoals] = useState(null);
  const [selectedFriendForMenu, setSelectedFriendForMenu] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState('general');
  const [showReflectionPrompt, setShowReflectionPrompt] = useState(false);
  const [reflectionData, setReflectionData] = useState(null);
  const [newFriend, setNewFriend] = useState({
    name: '',
    relationship: 'friend',
    lastContact: '',
    contactFrequency: 'weekly',
    notes: '',
    goals: []
  });
  const [newContact, setNewContact] = useState({
    type: 'call',
    date: '',
    duration: '',
    notes: ''
  });
  const [contactForm, setContactForm] = useState({
    type: 'call',
    date: new Date().toISOString().split('T')[0], // Today's date
    time: new Date().toTimeString().slice(0, 5), // Current time
    notes: '',
    plannedAppointment: false,
    appointmentDate: '',
    appointmentTime: '',
    appointmentTitle: '',
    appointmentLocation: '',
    createFollowUpReminder: false,
    conversationSummary: '',
    followUpQuestion: '',
    followUpDate: '',
    followUpTime: '',
    emailReminder: false
  });
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    category: '',
    targetDate: ''
  });

  // Test Firebase connection
  useEffect(() => {
    if (['friends', 'appointments', 'history'].includes(initialActiveTab)) {
      setActiveTab(initialActiveTab);
    }
  }, [initialActiveTab]);

  useEffect(() => {
    const testFirebaseConnection = async () => {
      if (currentUser && currentUser.uid) {
        try {
          console.log('Testing Firebase connection...');
          const testQuery = query(collection(db, 'contactHistory'), where('userId', '==', currentUser.uid));
          const testSnapshot = await getDocs(testQuery);
          console.log('Firebase connection test successful. Found', testSnapshot.docs.length, 'contact records');
        } catch (error) {
          console.error('Firebase connection test failed:', error);
        }
      }
    };
    
    testFirebaseConnection();
  }, [currentUser]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedFriendForMenu && !event.target.closest('.friend-menu')) {
        setSelectedFriendForMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedFriendForMenu]);

  // Load data from Firebase
  useEffect(() => {
    const loadData = async () => {
      if (currentUser && currentUser.uid) {
        console.log('Loading data for user:', currentUser.uid);
        try {
          // Load friends
          const friendsQuery = query(
            collection(db, 'friends'),
            where('userId', '==', currentUser.uid)
          );
          const friendsSnapshot = await getDocs(friendsQuery);
          const friendsData = friendsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setFriends(friendsData);
          console.log('Loaded friends:', friendsData.length, 'friends');

          // Preselect focused friend if provided
          if (focusFriendId) {
            const found = friendsData.find(f => f.id === focusFriendId);
            if (found) {
              setActiveTab('friends');
              setSelectedFriend(found);
              
              // Note: Friend details modal will be shown in a separate useEffect after data is loaded
            }
          }

          // Load appointments (without orderBy to avoid index issues, sort in JavaScript instead)
          try {
            const appointmentsQuery = query(
              collection(db, 'appointments'),
              where('userId', '==', currentUser.uid)
            );
            const appointmentsSnapshot = await getDocs(appointmentsQuery);
            const appointmentsData = appointmentsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })).sort((a, b) => {
              // Sort by date ascending
              const dateA = new Date(a.date || 0);
              const dateB = new Date(b.date || 0);
              return dateA - dateB;
            });
            setAppointments(appointmentsData);
            console.log('Loaded appointments:', appointmentsData.length, 'appointments');
          } catch (appointmentErr) {
            console.error('Error loading appointments:', appointmentErr);
            setAppointments([]);
          }

          // Load follow-up reminders
          try {
            const remindersQuery = query(
              collection(db, 'followUpReminders'),
              where('userId', '==', currentUser.uid)
            );
            const remindersSnapshot = await getDocs(remindersQuery);
            const remindersData = remindersSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })).sort((a, b) => {
              const dateA = new Date(`${a.reminderDate || '1970-01-01'}T${a.reminderTime || '00:00'}`);
              const dateB = new Date(`${b.reminderDate || '1970-01-01'}T${b.reminderTime || '00:00'}`);
              return dateA - dateB;
            });
            setFollowUpReminders(remindersData);
            console.log('Loaded follow-up reminders:', remindersData.length, 'reminders');
          } catch (reminderErr) {
            console.error('Error loading follow-up reminders:', reminderErr);
            setFollowUpReminders([]);
          }

          // Load contact history
          const historyQuery = query(
            collection(db, 'contactHistory'),
            where('userId', '==', currentUser.uid)
          );
          const historySnapshot = await getDocs(historyQuery);
          const historyData = historySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })).sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending
          setContactHistory(historyData);
          console.log('Loaded contact history:', historyData.length, 'records');
          console.log('Contact history data:', historyData);
          
          // Set data loaded after all data is loaded
          setDataLoaded(true);
          console.log('All data loaded successfully');
        } catch (error) {
          console.error('Error loading data:', error);
          setDataLoaded(true); // Set to true even on error to prevent infinite loading
        }
      } else {
        console.log('No current user, setting dataLoaded to true');
        setDataLoaded(true); // Set to true if no user
      }
    };

    loadData();
  }, [currentUser, reloadTrigger]);

  // Show friend details modal after data is loaded
  useEffect(() => {
    if (dataLoaded && showFriendDetailsOnLoad && focusFriendId && friends.length > 0) {
      const found = friends.find(f => f.id === focusFriendId);
      if (found) {
        setSelectedFriendForDetails(found);
        // Ensure contact history and reflections are loaded before opening modal
        console.log('Opening friend details modal on load, ensuring data is loaded...');
        if (currentUser && currentUser.uid) {
          const loadContactHistory = async () => {
            try {
              const historyQuery = query(
                collection(db, 'contactHistory'),
                where('userId', '==', currentUser.uid)
              );
              const historySnapshot = await getDocs(historyQuery);
              const historyData = historySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              })).sort((a, b) => new Date(b.date) - new Date(a.date));
              setContactHistory(historyData);
              console.log('Refreshed contact history for modal on load:', historyData.length, 'records');
              
              // Load reflections
              const reflectionsQuery = query(
                collection(db, 'reflections'),
                where('userId', '==', currentUser.uid)
              );
              const reflectionsSnapshot = await getDocs(reflectionsQuery);
              const reflectionsData = reflectionsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              setReflections(reflectionsData);
              console.log('Loaded reflections for modal on load:', reflectionsData.length, 'reflections');
              
              setShowFriendDetails(true);
            } catch (error) {
              console.error('Error refreshing data on load:', error);
              setShowFriendDetails(true); // Open modal even if refresh fails
            }
          };
          loadContactHistory();
        } else {
          setShowFriendDetails(true);
        }
      }
    }
  }, [dataLoaded, showFriendDetailsOnLoad, focusFriendId, friends, currentUser]);

  // Ensure contact history is loaded when history tab is active
  useEffect(() => {
    if (activeTab === 'history' && currentUser && contactHistory.length === 0) {
      const loadContactHistory = async () => {
        try {
          console.log('Loading contact history for history tab...');
          const historyQuery = query(
            collection(db, 'contactHistory'),
            where('userId', '==', currentUser.uid)
          );
          const historySnapshot = await getDocs(historyQuery);
          const historyData = historySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })).sort((a, b) => new Date(b.date) - new Date(a.date));
          setContactHistory(historyData);
          console.log('Loaded contact history for history tab:', historyData.length, 'records');
        } catch (error) {
          console.error('Error loading contact history for history tab:', error);
        }
      };
      loadContactHistory();
    }
  }, [activeTab, currentUser, contactHistory.length]);

  // Ensure appointments are loaded when appointments tab is active
  useEffect(() => {
    if (activeTab === 'appointments' && currentUser && currentUser.uid) {
      const loadAppointments = async () => {
        try {
          console.log('Loading appointments for appointments tab...');
          const appointmentsQuery = query(
            collection(db, 'appointments'),
            where('userId', '==', currentUser.uid)
          );
          const appointmentsSnapshot = await getDocs(appointmentsQuery);
          const appointmentsData = appointmentsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })).sort((a, b) => {
            // Sort by date ascending
            const dateA = new Date(a.date || 0);
            const dateB = new Date(b.date || 0);
            return dateA - dateB;
          });
          setAppointments(appointmentsData);
          console.log('Loaded appointments for appointments tab:', appointmentsData.length, 'appointments');
          console.log('Appointments data:', appointmentsData);

          const remindersQuery = query(
            collection(db, 'followUpReminders'),
            where('userId', '==', currentUser.uid)
          );
          const remindersSnapshot = await getDocs(remindersQuery);
          const remindersData = remindersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })).sort((a, b) => {
            const dateA = new Date(`${a.reminderDate || '1970-01-01'}T${a.reminderTime || '00:00'}`);
            const dateB = new Date(`${b.reminderDate || '1970-01-01'}T${b.reminderTime || '00:00'}`);
            return dateA - dateB;
          });
          setFollowUpReminders(remindersData);
          console.log('Loaded reminders for appointments tab:', remindersData.length, 'reminders');
        } catch (error) {
          console.error('Error loading appointments for appointments tab:', error);
        }
      };
      // Always load when tab becomes active to ensure fresh data
      loadAppointments();
    }
  }, [activeTab, currentUser]);

  const addFriend = async (e) => {
    e.preventDefault();
    if (!newFriend.name.trim()) return;

    // Check if user can add more friends
    const canAdd = await canAddFriend();
    if (!canAdd) {
      setPremiumModalReason('friend_limit');
      setShowPremiumModal(true);
      return;
    }

    // Verify user is authenticated
    if (!currentUser) {
      console.error('Cannot add friend: User not authenticated');
      alert('Error: You must be logged in to add a friend. Please refresh the page and try again.');
      return;
    }

    try {
      const friendData = {
        ...newFriend,
        userId: currentUser.uid,
        createdAt: new Date().toISOString(),
        lastContactDate: newFriend.lastContact || new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'friends'), friendData);
      
      setFriends([...friends, { id: docRef.id, ...friendData }]);
      setNewFriend({
        name: '',
        relationship: 'friend',
        lastContact: '',
        contactFrequency: 'weekly',
        notes: '',
        goals: []
      });
      setShowAddFriend(false);
    } catch (error) {
      console.error('Error adding friend:', error);
      
      // Show user-friendly error message
      let errorMessage = 'Failed to add friend. ';
      if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied. Please check that you are logged in and have the correct permissions.';
      } else if (error.code === 'unauthenticated') {
        errorMessage += 'You are not authenticated. Please log in again.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  const updateLastContact = async (friendId) => {
    try {
      const friendRef = doc(db, 'friends', friendId);
      const now = new Date().toISOString();
      await updateDoc(friendRef, {
        lastContactDate: now
      });

      setFriends(friends.map(friend => 
        friend.id === friendId 
          ? { ...friend, lastContactDate: now }
          : friend
      ));
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };

  const startEditNotes = (friend) => {
    setEditingFriendId(friend.id);
    setNotesDraft(friend.notes || '');
  };

  const cancelEditNotes = () => {
    setEditingFriendId(null);
    setNotesDraft('');
  };

  const saveFriendNotes = async (friendId) => {
    try {
      const friendRef = doc(db, 'friends', friendId);
      await updateDoc(friendRef, { notes: notesDraft });
      setFriends(friends.map(f => f.id === friendId ? { ...f, notes: notesDraft } : f));
      setEditingFriendId(null);
      setNotesDraft('');
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const deleteFriend = async (friendId) => {
    if (window.confirm('Are you sure you want to remove this friend from your tracker?')) {
      try {
        await deleteDoc(doc(db, 'friends', friendId));
        setFriends(friends.filter(friend => friend.id !== friendId));
      } catch (error) {
        console.error('Error deleting friend:', error);
      }
    }
  };


  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentRef, { status });
      
      setAppointments(appointments.map(apt => 
        apt.id === appointmentId 
          ? { ...apt, status }
          : apt
      ));

      // If completed, add to contact history and update friend's lastContactDate
      if (status === 'completed') {
        const appointment = appointments.find(apt => apt.id === appointmentId);
        if (appointment) {
          // Use today's date and time when marking as completed
          // The appointment date is when it was scheduled, but we're completing it today
          const contactDate = new Date();
          const contactDateISO = contactDate.toISOString();
          
          // Add to contact history with TODAY's date (when the appointment was actually completed)
          // This ensures the contact history shows when the interaction actually happened
          await addContactRecord(appointment.friendId, appointment.friendName, 'meeting', contactDateISO, '', appointment.notes);
          
          // Update friend's lastContactDate to TODAY (when the appointment was completed)
          const friendRef = doc(db, 'friends', appointment.friendId);
          await updateDoc(friendRef, {
            lastContactDate: contactDateISO
          });
          
          // Update local state
          setFriends(friends.map(friend => 
            friend.id === appointment.friendId 
              ? { ...friend, lastContactDate: contactDateISO }
              : friend
          ));
        }
      }
    } catch (error) {
      console.error('Error updating appointment status:', error);
    }
  };

  const deleteAppointment = async (appointmentId) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await deleteDoc(doc(db, 'appointments', appointmentId));
        setAppointments(appointments.filter(apt => apt.id !== appointmentId));
      } catch (error) {
        console.error('Error deleting appointment:', error);
      }
    }
  };

  const handleAppointmentAdded = (newAppointment) => {
    setAppointments([...appointments, newAppointment]);
  };

  const handleAppointmentDeleted = (appointmentId) => {
    setAppointments(appointments.filter(apt => apt.id !== appointmentId));
  };

  const updateFollowUpReminderStatus = async (reminderId, status) => {
    try {
      const reminderRef = doc(db, 'followUpReminders', reminderId);
      await updateDoc(reminderRef, {
        status,
        updatedAt: new Date().toISOString()
      });

      setFollowUpReminders(followUpReminders.map(reminder =>
        reminder.id === reminderId ? { ...reminder, status } : reminder
      ));
    } catch (error) {
      console.error('Error updating reminder status:', error);
    }
  };

  const addContactRecord = async (friendId, friendName, type, date, duration = '', notes = '') => {
    if (!currentUser) {
      console.error('User not authenticated, cannot save contact record');
      return;
    }
    
    try {
      const contactData = {
        friendId,
        friendName,
        type,
        date: date || new Date().toISOString(),
        duration,
        notes,
        userId: currentUser.uid,
        createdAt: new Date().toISOString()
      };
      
      console.log('Saving contact data:', contactData);
      console.log('friendId type:', typeof friendId, 'value:', friendId);
      console.log('friendName:', friendName);

      const docRef = await addDoc(collection(db, 'contactHistory'), contactData);
      const newRecord = { id: docRef.id, ...contactData };
      setContactHistory([newRecord, ...contactHistory]);
      console.log('Added contact record:', newRecord);
      console.log('Updated contactHistory state:', [newRecord, ...contactHistory]);
    } catch (error) {
      console.error('Error adding contact record:', error);
    }
  };

  const logContact = async (friendId, friendName, type, duration = '', notes = '') => {
    console.log('Logging contact:', { friendId, friendName, type, duration, notes });
    await addContactRecord(friendId, friendName, type, new Date().toISOString(), duration, notes);
    
    // Update last contact date for the friend
    const friendRef = doc(db, 'friends', friendId);
    await updateDoc(friendRef, {
      lastContactDate: new Date().toISOString()
    });

    setFriends(friends.map(friend => 
      friend.id === friendId 
        ? { ...friend, lastContactDate: new Date().toISOString() }
        : friend
    ));

    // Show reflection prompt for meaningful interactions
    console.log('Checking if should show reflection prompt for type:', type);
    if (['call', 'meeting', 'video', 'lunch', 'dinner', 'coffee', 'activity', 'email', 'text'].includes(type)) {
      console.log('Triggering reflection prompt for:', { friendId, friendName, contactType: type });
      setReflectionData({
        friendId,
        friendName,
        contactType: type
      });
      setShowReflectionPrompt(true);
    } else {
      console.log('Not showing reflection prompt for type:', type);
    }
    
    // Test: Try to reload contact history to verify it was saved
    setTimeout(async () => {
      try {
        const testQuery = query(
          collection(db, 'contactHistory'),
          where('userId', '==', currentUser.uid)
        );
        const testSnapshot = await getDocs(testQuery);
        const testData = testSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => new Date(b.date) - new Date(a.date));
        console.log('Test reload - contact history:', testData.length, 'records');
        console.log('Test reload - latest record:', testData[0]);
      } catch (error) {
        console.error('Test reload error:', error);
      }
    }, 1000);
  };

  const saveContactForm = async (e) => {
    e.preventDefault();
    if (!selectedFriendForContact || isSavingContact) return;

    if (!contactForm.date || !contactForm.time) {
      alert('Please add both date and time before saving.');
      return;
    }
    if (contactForm.plannedAppointment && (!contactForm.appointmentTitle || !contactForm.appointmentDate || !contactForm.appointmentTime)) {
      alert('Please complete all appointment fields before saving.');
      return;
    }
    if (contactForm.createFollowUpReminder && (!contactForm.followUpQuestion || !contactForm.followUpDate)) {
      alert('Please add a follow-up question and reminder date before saving.');
      return;
    }

    let contactSaved = false;
    setIsSavingContact(true);
    try {
      // Create the contact date/time
      const contactDateTime = new Date(`${contactForm.date}T${contactForm.time}`);
      if (isNaN(contactDateTime.getTime())) {
        alert('The contact date or time is invalid. Please check and try again.');
        return;
      }
      
      // Add the contact record
      await addContactRecord(
        selectedFriendForContact.id,
        selectedFriendForContact.name,
        contactForm.type,
        contactDateTime.toISOString(),
        '',
        contactForm.notes
      );
      contactSaved = true;

      // If they planned an appointment, add it to appointments
      if (contactForm.plannedAppointment && contactForm.appointmentTitle && contactForm.appointmentDate) {
        try {
          const appointmentData = {
            title: contactForm.appointmentTitle,
            date: contactForm.appointmentDate,
            time: contactForm.appointmentTime,
            location: contactForm.appointmentLocation,
            notes: `Planned during ${contactForm.type} on ${contactForm.date}`,
            type: 'meeting',
            friendId: selectedFriendForContact.id,
            friendName: selectedFriendForContact.name,
            userId: currentUser.uid,
            status: 'scheduled',
            createdAt: new Date().toISOString()
          };

          const docRef = await addDoc(collection(db, 'appointments'), appointmentData);
          setAppointments([...appointments, { id: docRef.id, ...appointmentData }]);
        } catch (appointmentError) {
          console.error('Error saving planned appointment:', appointmentError);
        }
      }

      // If they captured important conversation notes, create a follow-up reminder
      if (contactForm.createFollowUpReminder && contactForm.followUpQuestion && contactForm.followUpDate) {
        try {
          const reminderData = {
            friendId: selectedFriendForContact.id,
            friendName: selectedFriendForContact.name,
            userId: currentUser.uid,
            sourceContactType: contactForm.type,
            conversationSummary: contactForm.conversationSummary || '',
            followUpQuestion: contactForm.followUpQuestion,
            reminderDate: contactForm.followUpDate,
            reminderTime: contactForm.followUpTime || '09:00',
            sendEmailReminder: !!contactForm.emailReminder,
            emailSent: false,
            status: 'pending',
            createdAt: new Date().toISOString()
          };

          const reminderRef = await addDoc(collection(db, 'followUpReminders'), reminderData);
          setFollowUpReminders(prev => [...prev, { id: reminderRef.id, ...reminderData }]);
        } catch (reminderError) {
          console.error('Error saving follow-up reminder:', reminderError);
          const reminderErrorCode = reminderError?.code || 'unknown-error';
          const reminderErrorMessage = reminderError?.message || 'No additional details';
          alert(`Your interaction was saved, but the follow-up reminder failed (${reminderErrorCode}). ${reminderErrorMessage}`);
        }
      }

      // Update last contact date for the friend
      try {
        const friendRef = doc(db, 'friends', selectedFriendForContact.id);
        await updateDoc(friendRef, {
          lastContactDate: contactDateTime.toISOString()
        });

        setFriends(friends.map(friend => 
          friend.id === selectedFriendForContact.id 
            ? { ...friend, lastContactDate: contactDateTime.toISOString() }
            : friend
        ));
      } catch (friendUpdateError) {
        console.error('Error updating friend last contact date:', friendUpdateError);
      }

      // Show reflection prompt for meaningful interactions
      if (['call', 'meeting', 'video', 'lunch', 'dinner', 'coffee', 'activity', 'email', 'text'].includes(contactForm.type)) {
        setReflectionData({
          friendId: selectedFriendForContact.id,
          friendName: selectedFriendForContact.name,
          contactType: contactForm.type
        });
        setShowReflectionPrompt(true);
      }

      // Reset form and close modal
      setContactForm({
        type: 'call',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        notes: '',
        plannedAppointment: false,
        appointmentDate: '',
        appointmentTime: '',
        appointmentTitle: '',
        appointmentLocation: '',
        createFollowUpReminder: false,
        conversationSummary: '',
        followUpQuestion: '',
        followUpDate: '',
        followUpTime: '',
        emailReminder: false
      });
      setShowContactModal(false);
      setSelectedFriendForContact(null);
    } catch (error) {
      console.error('Error saving contact:', error);
      if (!contactSaved) {
        alert('Could not save interaction. Please try again.');
      }
    } finally {
      setIsSavingContact(false);
    }
  };

  const deleteContactRecord = async (contactId) => {
    if (window.confirm(t('deleteContactRecord'))) {
      try {
        await deleteDoc(doc(db, 'contactHistory', contactId));
        setContactHistory(contactHistory.filter(contact => contact.id !== contactId));
      } catch (error) {
        console.error('Error deleting contact record:', error);
      }
    }
  };

  const reloadContactHistory = async () => {
    if (!currentUser || !currentUser.uid) {
      console.error('User not authenticated, cannot reload contact history');
      return;
    }
    
    try {
      console.log('Manually reloading contact history...');
      const historyQuery = query(
        collection(db, 'contactHistory'),
        where('userId', '==', currentUser.uid)
      );
      const historySnapshot = await getDocs(historyQuery);
      const historyData = historySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setContactHistory(historyData);
      console.log('Manually reloaded contact history:', historyData.length, 'records');
      console.log('Contact history data:', historyData);
    } catch (error) {
      console.error('Error manually reloading contact history:', error);
    }
  };

  const testContactSaveAndLoad = async () => {
    if (!currentUser || !currentUser.uid || friends.length === 0) {
      console.error('Cannot test - no user or no friends');
      return;
    }
    
    const testFriend = friends[0];
    console.log('Testing contact save and load with friend:', testFriend);
    
    try {
      // Create a test contact record
      const testContactData = {
        friendId: testFriend.id,
        friendName: testFriend.name,
        type: 'test',
        date: new Date().toISOString(),
        duration: '5 minutes',
        notes: 'This is a test contact record',
        userId: currentUser.uid,
        createdAt: new Date().toISOString()
      };
      
      console.log('Creating test contact record:', testContactData);
      const docRef = await addDoc(collection(db, 'contactHistory'), testContactData);
      console.log('Test contact record created with ID:', docRef.id);
      
      // Wait a moment and then reload
      setTimeout(async () => {
        try {
          const testQuery = query(
            collection(db, 'contactHistory'),
            where('userId', '==', currentUser.uid)
          );
          const testSnapshot = await getDocs(testQuery);
          const testData = testSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          console.log('Test reload - found', testData.length, 'contact records');
          const testRecord = testData.find(r => r.id === docRef.id);
          console.log('Test record found:', testRecord);
          
          if (testRecord) {
            console.log('✅ Test PASSED - contact record was saved and loaded successfully');
          } else {
            console.log('❌ Test FAILED - contact record was not found after reload');
          }
        } catch (error) {
          console.error('Test reload error:', error);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Test save error:', error);
    }
  };

  const getContactTypeLabel = (type) => {
    const typeMap = {
      'call': t('contactTypeCall'),
      'email': t('contactTypeEmail'),
      'text': t('contactTypeText'),
      'video': t('contactTypeVideo'),
      'meeting': t('contactTypeMeeting'),
      'other': t('contactTypeOther')
    };
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatDaysSince = (daysSince) => {
    if (daysSince === 0) return t('today');
    if (daysSince === 1) return t('oneDayAgo');
    return t('daysAgoCount', { count: daysSince });
  };

  const getRelationshipIcon = (relationship) => {
    const icons = {
      'friend': '👫',
      'best-friend': '💕',
      'colleague': '👔',
      'family': '👨‍👩‍👧‍👦',
      'acquaintance': '👋',
      'mentor': '🧠',
      'neighbor': '🏠'
    };
    return icons[relationship] || '👫';
  };


  const assignJourney = async (friendId, journeyType) => {
    try {
      const friendRef = doc(db, 'friends', friendId);
      const journeyId = `${journeyType}-${Date.now()}`;
      const newJourney = createDeepeningJourney(journeyId, journeyType);

      const friendSnap = await getDoc(friendRef);
      const existingJourneys = friendSnap.exists() ? (friendSnap.data().journeys || []) : [];

      await updateDoc(friendRef, {
        journeys: [...existingJourneys, newJourney]
      });

      setFriends(prev =>
        prev.map(f =>
          f.id === friendId
            ? {
                ...f,
                journeys: [...existingJourneys, newJourney]
              }
            : f
        )
      );

      return journeyId;
    } catch (error) {
      console.error('Error assigning journey:', error);
      return null;
    }
  };

  // Goals Management Functions
  const addGoal = async (e) => {
    e.preventDefault();
    if (!selectedFriendForGoals || !newGoal.title.trim() || !newGoal.category) return;

    try {
      const friendRef = doc(db, 'friends', selectedFriendForGoals.id);
      const goalId = `goal-${Date.now()}`;
      const goalData = {
        id: goalId,
        title: newGoal.title,
        description: newGoal.description,
        category: newGoal.category,
        targetDate: newGoal.targetDate,
        createdAt: new Date().toISOString(),
        status: 'active'
      };

      const existingGoals = selectedFriendForGoals.goals || [];
      await updateDoc(friendRef, { 
        goals: [...existingGoals, goalData]
      });
      
      setFriends(friends.map(f => 
        f.id === selectedFriendForGoals.id 
          ? { ...f, goals: [...existingGoals, goalData] }
          : f
      ));
      
      setNewGoal({
        title: '',
        description: '',
        category: '',
        targetDate: ''
      });
      setShowGoalsModal(false);
      setSelectedFriendForGoals(null);
    } catch (error) {
      console.error('Error adding goal:', error);
    }
  };


  const getGoalCategoryLabel = (category) => {
    const categoryMap = {
      'connection': t('goalCategoryConnection'),
      'communication': t('goalCategoryCommunication'),
      'fun': t('goalCategoryFun'),
      'support': t('goalCategorySupport'),
      'growth': t('goalCategoryGrowth'),
      'other': t('goalCategoryOther')
    };
    return categoryMap[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  const deleteGoal = async (friendId, goalId) => {
    if (window.confirm(t('confirmDeleteGoal'))) {
      try {
        const friendRef = doc(db, 'friends', friendId);
        const friend = friends.find(f => f.id === friendId);
        const updatedGoals = friend.goals.filter(goal => goal.id !== goalId);
        
        await updateDoc(friendRef, { goals: updatedGoals });
        setFriends(friends.map(f => 
          f.id === friendId ? { ...f, goals: updatedGoals } : f
        ));
      } catch (error) {
        console.error('Error deleting goal:', error);
      }
    }
  };

  const getJourneyProgress = (friend) => {
    if (!friend.journeys || friend.journeys.length === 0) return null;
    
    // Get the most recent active journey
    const activeJourneys = friend.journeys.filter(j => j.status === 'active');
    if (activeJourneys.length === 0) return null;
    
    // Use correct totals for each week (don't rely on stored totals which may be outdated)
    const correctTotals = {
      week1: 6,
      week2: 4,
      week3: 4,
      week4: 3
    };
    
    const latestJourney = activeJourneys[activeJourneys.length - 1];
    const totalCompleted = Object.values(latestJourney.progress).reduce((sum, week) => sum + week.completed, 0);
    // Calculate total using correct week totals instead of stored totals
    const totalActivities = Object.keys(correctTotals).reduce((sum, weekKey) => {
      return sum + (correctTotals[weekKey] || 0);
    }, 0);
    
    return {
      completed: totalCompleted,
      total: totalActivities,
      percentage: Math.round((totalCompleted / totalActivities) * 100),
      journeyType: latestJourney.type,
      journeyId: latestJourney.id
    };
  };

  const getJourneySummary = (friend) => {
    if (!friend.journeys || friend.journeys.length === 0) return null;
    
    // Use correct totals for each week (don't rely on stored totals which may be outdated)
    const correctTotals = {
      week1: 6,
      week2: 4,
      week3: 4,
      week4: 3
    };
    
    const activeJourneys = friend.journeys.filter(j => j.status === 'active');
    const completedJourneys = friend.journeys.filter(j => j.status === 'completed');
    const totalJourneys = friend.journeys.length;
    
    // Calculate total using correct week totals
    const totalActivities = Object.keys(correctTotals).reduce((sum, weekKey) => {
      return sum + (correctTotals[weekKey] || 0);
    }, 0);
    
    if (totalJourneys === 1) {
      // Single journey - show progress
      const journey = friend.journeys[0];
      const totalCompleted = Object.values(journey.progress).reduce((sum, week) => sum + week.completed, 0);
      const percentage = Math.round((totalCompleted / totalActivities) * 100);
      
      return {
        display: `${percentage}%`,
        tooltip: `${journey.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} - ${totalCompleted}/${totalActivities} activities`
      };
    } else if (activeJourneys.length > 0) {
      // Multiple journeys with active ones
      const latestActive = activeJourneys[activeJourneys.length - 1];
      const totalCompleted = Object.values(latestActive.progress).reduce((sum, week) => sum + week.completed, 0);
      const percentage = Math.round((totalCompleted / totalActivities) * 100);
      
      // Calculate percentages for all active journeys
      const activePercentages = activeJourneys.map(journey => {
        const completed = Object.values(journey.progress).reduce((sum, week) => sum + week.completed, 0);
        return Math.round((completed / totalActivities) * 100);
      });
      
      const percentagesText = activePercentages.join('%, ') + '%';
      
      return {
        display: `${activeJourneys.length} active (${percentagesText})`,
        tooltip: `Active journeys: ${activePercentages.map((p, i) => `${p}%`).join(', ')}. Total: ${totalJourneys} journeys (${completedJourneys.length} completed)`
      };
    } else {
      // All completed
      return {
        display: `${completedJourneys.length} completed`,
        tooltip: `All ${totalJourneys} journeys completed`
      };
    }
  };

  const handleSaveReflection = async (reflection) => {
    console.log('handleSaveReflection called with:', reflection);
    console.log('currentUser:', currentUser);
    
    if (!currentUser) {
      console.error('No current user, cannot save reflection');
      return;
    }
    
    try {
      const reflectionRecord = {
        ...reflection,
        userId: currentUser.uid,
        createdAt: new Date().toISOString()
      };
      
      console.log('Saving reflection record:', reflectionRecord);
      const docRef = await addDoc(collection(db, 'reflections'), reflectionRecord);
      console.log('Reflection saved successfully with ID:', docRef.id);
      
      // Reload reflections to include the new one
      const reflectionsQuery = query(
        collection(db, 'reflections'),
        where('userId', '==', currentUser.uid)
      );
      const reflectionsSnapshot = await getDocs(reflectionsQuery);
      const reflectionsData = reflectionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReflections(reflectionsData);
      
      // You could also update the contact record with the reflection
      // or trigger milestone checks based on the reflection content
    } catch (error) {
      console.error('Error saving reflection:', error);
      console.error('Error details:', error.message, error.code);
    }
  };

  const findReflectionForInteraction = (interaction) => {
    if (!interaction || !interaction.friendName || !interaction.displayType) return null;
    
    // Match reflection by friendName, contactType, and date proximity (within 1 hour)
    const interactionDate = new Date(interaction.date);
    
    return reflections.find(reflection => {
      // Match friend name and contact type
      const nameMatches = reflection.friendName === interaction.friendName;
      const typeMatches = reflection.contactType === interaction.displayType;
      
      if (!nameMatches || !typeMatches) return false;
      
      // Match by date proximity (within 1 hour)
      const reflectionDate = new Date(reflection.timestamp || reflection.createdAt);
      const timeDiff = Math.abs(interactionDate.getTime() - reflectionDate.getTime());
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      return hoursDiff <= 1; // Within 1 hour
    });
  };

  const formatReflectionTopics = (topics) => {
    if (!topics || topics.length === 0) return 'None';
    return topics.map(topic => {
      if (topic.startsWith('Other: ')) {
        return topic.replace('Other: ', '');
      }
      return topic;
    }).join(', ');
  };

  return (
    <div className="friendship-tracker">
      <div className="tracker-header">
        <button className="back-button" onClick={onBack}>
          ← {t('back')}
        </button>
        

        <h1>{t('friendshipTracker')}</h1>
        <p>{t('trackerSubtitle')}</p>
        
      </div>

      <div className="tracker-stats">
        <div className="stat-card">
          <div className="stat-number">{friends.length}</div>
          <div className="stat-label">{t('friends')}</div>
        </div>
         <div className="stat-card">
          <div className="stat-number">
            {friends.filter(friend => getContactStatus(friend).status === 'healthy').length}
          </div>
          <div className="stat-label">{t('healthyRelationships')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {friends.filter(friend => getContactStatus(friend, t).status === 'attention').length}
          </div>
          <div className="stat-label">{t('timeToTakeActionTracker')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {friends.filter(friend => getContactStatus(friend, t).status === 'urgent').length}
          </div>
          <div className="stat-label">{t('needAttentionTracker')}</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tracker-tabs">
        <button 
          className={`tab-button ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          👥 {t('friends')}
        </button>
        <button 
          className={`tab-button ${activeTab === 'appointments' ? 'active' : ''}`}
          onClick={async () => {
            setActiveTab('appointments');
            // Refresh appointments when tab is clicked
            if (currentUser && currentUser.uid) {
              try {
                console.log('Refreshing appointments for appointments tab...');
                const appointmentsQuery = query(
                  collection(db, 'appointments'),
                  where('userId', '==', currentUser.uid)
                );
                const appointmentsSnapshot = await getDocs(appointmentsQuery);
                const appointmentsData = appointmentsSnapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                })).sort((a, b) => {
                  // Sort by date ascending
                  const dateA = new Date(a.date || 0);
                  const dateB = new Date(b.date || 0);
                  return dateA - dateB;
                });
                setAppointments(appointmentsData);
                console.log('Refreshed appointments for tab:', appointmentsData.length, 'appointments');

                const remindersQuery = query(
                  collection(db, 'followUpReminders'),
                  where('userId', '==', currentUser.uid)
                );
                const remindersSnapshot = await getDocs(remindersQuery);
                const remindersData = remindersSnapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                })).sort((a, b) => {
                  const dateA = new Date(`${a.reminderDate || '1970-01-01'}T${a.reminderTime || '00:00'}`);
                  const dateB = new Date(`${b.reminderDate || '1970-01-01'}T${b.reminderTime || '00:00'}`);
                  return dateA - dateB;
                });
                setFollowUpReminders(remindersData);
                console.log('Refreshed follow-up reminders:', remindersData.length, 'reminders');
              } catch (error) {
                console.error('Error refreshing appointments for tab:', error);
              }
            }
          }}
        >
          📅 {t('appointments')}
        </button>
        <button 
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={async () => {
            setActiveTab('history');
            // Refresh contact history when tab is clicked
            if (currentUser && currentUser.uid) {
              try {
                console.log('Refreshing contact history for history tab...');
                const historyQuery = query(
                  collection(db, 'contactHistory'),
                  where('userId', '==', currentUser.uid)
                );
                const historySnapshot = await getDocs(historyQuery);
                const historyData = historySnapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                })).sort((a, b) => new Date(b.date) - new Date(a.date));
                setContactHistory(historyData);
                console.log('Refreshed contact history for tab:', historyData.length, 'records');
              } catch (error) {
                console.error('Error refreshing contact history for tab:', error);
              }
            }
          }}
        >
          📝 {t('contactHistory')}
        </button>
      </div>

      <div className="tracker-actions">
        {activeTab === 'friends' && (
          <button 
            className="add-friend-btn"
            onClick={() => setShowAddFriend(true)}
          >
            + {t('addFriend')} {!isPremium && t('addFriendWithLimit', { count: friends.length })}
          </button>
        )}
        {activeTab === 'appointments' && (
          <button 
            className="add-friend-btn"
            onClick={() => setShowScheduleAppointment(true)}
          >
            + {t('scheduleAppointment')}
          </button>
        )}

      </div>

      {showAddFriend && (
        <div className="add-friend-modal">
          <div className="modal-content">
            <h3>{t('addNewFriend')}</h3>
            <form onSubmit={addFriend}>
              <div className="form-group">
                <label>{t('nameRequired')}</label>
                <input
                  type="text"
                  value={newFriend.name}
                  onChange={(e) => setNewFriend({...newFriend, name: e.target.value})}
                  placeholder={t('friendsName')}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('relationship')}</label>
                  <select
                    value={newFriend.relationship}
                    onChange={(e) => setNewFriend({...newFriend, relationship: e.target.value})}
                  >
                    <option value="friend">{t('friend')}</option>
                    <option value="best-friend">{t('bestFriend')}</option>
                    <option value="colleague">{t('colleague')}</option>
                    <option value="family">{t('family')}</option>
                    <option value="acquaintance">{t('acquaintance')}</option>
                    <option value="mentor">{t('mentor')}</option>
                    <option value="neighbor">{t('neighbor')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('contactFrequency')}</label>
                  <select
                    value={newFriend.contactFrequency}
                    onChange={(e) => setNewFriend({...newFriend, contactFrequency: e.target.value})}
                  >
                    <option value="daily">{t('daily')}</option>
                    <option value="weekly">{t('weekly')}</option>
                    <option value="biweekly">{t('biweekly')}</option>
                    <option value="monthly">{t('monthly')}</option>
                    <option value="quarterly">{t('quarterly')}</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>{t('lastContactDate')}</label>
                <input
                  type="date"
                  value={newFriend.lastContact}
                  onChange={(e) => setNewFriend({...newFriend, lastContact: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>{t('notes')}</label>
                <textarea
                  value={newFriend.notes}
                  onChange={(e) => setNewFriend({...newFriend, notes: e.target.value})}
                  placeholder={t('notesPlaceholder')}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>{t('initialGoalsOptional')}</label>
                <div style={{ marginBottom: '15px' }}>
                  <input
                    type="text"
                    placeholder={t('goalTitlePlaceholder')}
                    value={newFriend.goals ? newFriend.goals[newFriend.goals.length - 1]?.title || '' : ''}
                    onChange={(e) => {
                      const currentGoals = newFriend.goals || [];
                      if (currentGoals.length === 0) {
                        // Add first goal
                        setNewFriend({
                          ...newFriend, 
                          goals: [{
                            id: `goal-${Date.now()}-${Math.random()}`,
                            title: e.target.value,
                            description: '',
                            category: 'connection',
                            targetDate: '',
                            createdAt: new Date().toISOString(),
                            status: 'active'
                          }]
                        });
                      } else {
                        // Update last goal
                        const updatedGoals = [...currentGoals];
                        updatedGoals[updatedGoals.length - 1] = {
                          ...updatedGoals[updatedGoals.length - 1],
                          title: e.target.value
                        };
                        setNewFriend({...newFriend, goals: updatedGoals});
                      }
                    }}
                    style={{ marginBottom: '10px' }}
                  />
                  {newFriend.goals && newFriend.goals.length > 0 && (
                    <select
                      value={newFriend.goals[newFriend.goals.length - 1]?.category || 'connection'}
                      onChange={(e) => {
                        const currentGoals = newFriend.goals || [];
                        const updatedGoals = [...currentGoals];
                        updatedGoals[updatedGoals.length - 1] = {
                          ...updatedGoals[updatedGoals.length - 1],
                          category: e.target.value
                        };
                        setNewFriend({...newFriend, goals: updatedGoals});
                      }}
                      style={{ marginBottom: '10px' }}
                    >
                      <option value="connection">{t('connection')}</option>
                      <option value="communication">{t('communication')}</option>
                      <option value="fun">{t('funActivities')}</option>
                      <option value="support">{t('support')}</option>
                      <option value="growth">{t('personalGrowth')}</option>
                      <option value="other">{t('other')}</option>
                    </select>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const currentGoals = newFriend.goals || [];
                      if (currentGoals.length > 0 && currentGoals[currentGoals.length - 1].title.trim()) {
                        // Add new empty goal
                        setNewFriend({
                          ...newFriend,
                          goals: [...currentGoals, {
                            id: `goal-${Date.now()}-${Math.random()}`,
                            title: '',
                            description: '',
                            category: 'connection',
                            targetDate: '',
                            createdAt: new Date().toISOString(),
                            status: 'active'
                          }]
                        });
                      }
                    }}
                    style={{
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    + {t('addAnotherGoal')}
                  </button>
                  {newFriend.goals && newFriend.goals.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const currentGoals = newFriend.goals || [];
                        setNewFriend({
                          ...newFriend,
                          goals: currentGoals.slice(0, -1)
                        });
                      }}
                      style={{
                        background: '#fee2e2',
                        border: '1px solid #fca5a5',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        color: '#dc2626'
                      }}
                    >
                      {t('removeLastGoal')}
                    </button>
                  )}
                </div>
                {newFriend.goals && newFriend.goals.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <small style={{ color: '#6B7280', fontSize: '0.8rem', display: 'block', marginBottom: '5px' }}>
                      {t('currentGoals')}
                    </small>
                    {newFriend.goals.map((goal, index) => (
                      <div key={goal.id} style={{ 
                        fontSize: '0.8rem', 
                        color: '#374151', 
                        marginBottom: '3px',
                        padding: '3px 6px',
                        background: '#f9fafb',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}>
                        {index + 1}. {goal.title} ({goal.category})
                      </div>
                    ))}
                  </div>
                )}
                <small style={{ color: '#6B7280', fontSize: '0.8rem' }}>
                  {t('canAddMultipleGoals')}
                </small>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowAddFriend(false)}>
                  {t('cancel')}
                </button>
                <button type="submit">{t('addFriend')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={showScheduleAppointment}
        onClose={() => {
          setShowScheduleAppointment(false);
          setSelectedFriend(null);
        }}
        friend={selectedFriend}
        appointments={appointments}
        onAppointmentAdded={handleAppointmentAdded}
        onAppointmentDeleted={handleAppointmentDeleted}
      />

      {/* Tab Content */}
      {activeTab === 'friends' && (
        <div className="friends-list">
          {friends.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>{t('noFriendsTrackedYet')}</h3>
              <p>{t('addFirstFriendToStart')}</p>
            </div>
          ) : (
            friends.map(friend => {
              const contactStatus = getContactStatus(friend, t);
              const daysSince = getDaysSinceLastContact(friend.lastContactDate);
              const isAccessible = isFriendAccessible(friend.id, friends);
              
              return (
                <div 
                  key={friend.id} 
                  className={`friend-card ${contactStatus.status} ${!isAccessible ? 'friend-locked' : ''}`}
                  onClick={(e) => {
                    // Don't trigger if clicking on buttons, menu, dropdown, or notes editor
                    if (e.target.closest('.friend-menu') || 
                        e.target.closest('.friend-dropdown-menu') ||
                        e.target.closest('.quick-action-btn') ||
                        e.target.closest('.quick-action-wrapper') ||
                        e.target.closest('.friend-actions') ||
                        e.target.closest('.friend-notes-editor')) {
                      return;
                    }
                    // Only trigger if friend is accessible and onFriendClick is provided
                    if (isAccessible && onFriendClick) {
                      onFriendClick(friend);
                    }
                  }}
                  style={{ cursor: isAccessible && onFriendClick ? 'pointer' : 'default' }}
                >
                  <div className="friend-header">
                    <div className="friend-info">
                      <div className="friend-avatar">
                        {getRelationshipIcon(friend.relationship)}
                      </div>
                      <div className="friend-details">
                        <h3>{friend.name}</h3>
                        <p className="relationship-type">
                          {friend.relationship.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                      </div>
                    </div>
                    <div className="friend-actions">
                      <div 
                        className="status-indicator"
                        style={{ backgroundColor: contactStatus.color }}
                      >
                        <span className="status-text">
                          {contactStatus.message}
                        </span>
                      </div>
                      <div className="friend-menu">
                        <button 
                          className="menu-trigger"
                          onClick={() => isAccessible && setSelectedFriendForMenu(selectedFriendForMenu === friend.id ? null : friend.id)}
                          disabled={!isAccessible}
                        >
                          ⋯
                        </button>
                        {selectedFriendForMenu === friend.id && (
                          <div className="friend-dropdown-menu">
                            <button onClick={() => {
                              setSelectedFriendForGoals(friend);
                              setShowGoalsModal(true);
                              setSelectedFriendForMenu(null);
                            }}>
                              🎯 {friend.goals && friend.goals.length > 0 ? t('manageGoals') : t('setGoals')}
                            </button>
                            <button onClick={() => {
                              startEditNotes(friend);
                              setSelectedFriendForMenu(null);
                            }}>
                              ✏️ {t('editNotes')}
                            </button>
                            <button onClick={() => {
                              deleteFriend(friend.id);
                              setSelectedFriendForMenu(null);
                            }} className="danger">
                              🗑️ {t('removeFriend')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="friend-summary">
                    <div className="info-box">
                      <div className="info-icon">📅</div>
                      <div className="info-content">
                        <div className="info-label">{t('lastContact').replace(':', '')}</div>
                        <div className="info-value">{formatDaysSince(daysSince)}</div>
                      </div>
                    </div>
                    <div className="info-box">
                      <div className="info-icon">🔄</div>
                      <div className="info-content">
                        <div className="info-label">{t('contactFrequencyLabel').replace(':', '')}</div>
                        <div className="info-value">
                          {friend.contactFrequency.replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                      </div>
                    </div>
                    {friend.goals && friend.goals.length > 0 && (
                      <div className="info-box">
                        <div className="info-icon">🎯</div>
                        <div className="info-content">
                          <div className="info-label">{t('goals')}</div>
                          <div className="info-value">
                            {friend.goals.slice(0, 2).map(goal => goal.title).join(', ')}
                            {friend.goals.length > 2 && ` +${friend.goals.length - 2} more`}
                          </div>
                        </div>
                      </div>
                    )}
                    {friend.journeys && friend.journeys.length > 0 && (
                      <div className="info-box" title={getJourneySummary(friend)?.tooltip}>
                        <div className="info-icon">🛤️</div>
                        <div className="info-content">
                          <div className="info-label">{t('journeys')}</div>
                          <div className="info-value">
                            {getJourneySummary(friend)?.display || 'Active'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>


                  {friend.notes && (
                    <div className="friend-notes-preview">
                      <p>"{friend.notes}"</p>
                    </div>
                  )}

                  <div className="friend-quick-actions">
                    <div className="quick-action-wrapper">
                      <button 
                        className="quick-action-btn"
                        onClick={() => {
                          if (isAccessible) {
                            setSelectedFriendForContact(friend);
                            setShowContactModal(true);
                          }
                        }}
                        title="Log contact"
                        disabled={!isAccessible}
                      >
                        ✅ {t('logInteraction')}
                      </button>
                    </div>
                    <div className="quick-action-wrapper">
                      <button 
                        className="quick-action-btn"
                        onClick={() => {
                          if (isAccessible) {
                            // Set friend first, then open modal to ensure friend is available
                            setSelectedFriend(friend);
                            // Use setTimeout to ensure state update happens before modal opens
                            setTimeout(() => {
                              setShowScheduleAppointment(true);
                            }, 0);
                          }
                        }}
                        title="Schedule meeting"
                        disabled={!isAccessible}
                      >
                        📅 Schedule
                      </button>
                    </div>
                    <div className="quick-action-wrapper">
                      <button 
                        className="quick-action-btn"
                        onClick={() => {
                          if (isAccessible) {
                            setSelectedFriendForJourney(friend);
                            setShowJourneyModal(true);
                          }
                        }}
                        title="Start or view journey"
                        disabled={!isAccessible}
                      >
                        🛤️ {friend.journeys && friend.journeys.length > 0 ? t('journeyProgress') : t('startJourney')}
                      </button>
                    </div>
                    <div className="quick-action-wrapper">
                      <button 
                        className="quick-action-btn"
                        onClick={async () => {
                          if (isAccessible) {
                            setSelectedFriendForDetails(friend);
                            setSelectedFriendForMenu(null);
                            
                            // Ensure contact history and reflections are loaded before opening modal
                            console.log('Opening friend details modal, ensuring data is loaded...');
                            if (currentUser && currentUser.uid) {
                              try {
                                const historyQuery = query(
                                  collection(db, 'contactHistory'),
                                  where('userId', '==', currentUser.uid)
                                );
                                const historySnapshot = await getDocs(historyQuery);
                                const historyData = historySnapshot.docs.map(doc => ({
                                  id: doc.id,
                                  ...doc.data()
                                })).sort((a, b) => new Date(b.date) - new Date(a.date));
                                setContactHistory(historyData);
                                console.log('Refreshed contact history for modal:', historyData.length, 'records');
                                
                                // Load reflections
                                const reflectionsQuery = query(
                                  collection(db, 'reflections'),
                                  where('userId', '==', currentUser.uid)
                                );
                                const reflectionsSnapshot = await getDocs(reflectionsQuery);
                                const reflectionsData = reflectionsSnapshot.docs.map(doc => ({
                                  id: doc.id,
                                  ...doc.data()
                                }));
                                setReflections(reflectionsData);
                                console.log('Loaded reflections for modal:', reflectionsData.length, 'reflections');
                              } catch (error) {
                                console.error('Error refreshing data:', error);
                              }
                            }
                            
                            setShowFriendDetails(true);
                          }
                        }}
                        title="View friend details"
                        disabled={!isAccessible}
                      >
                        👁️ {t('interactionHistory')}
                      </button>
                    </div>
                  </div>

                  {editingFriendId === friend.id && (
                    <div className="friend-notes-editor">
                      <textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder={t('notesPlaceholderFriend')}
                        rows="3"
                        style={{ width: '100%', marginBottom: '10px' }}
                      />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-primary" onClick={() => saveFriendNotes(friend.id)}>{t('save')}</button>
                        <button className="btn btn-secondary" onClick={cancelEditNotes}>{t('cancel')}</button>
                      </div>
                    </div>
                  )}

                  {/* Premium Lock Overlay */}
                  {!isAccessible && (
                    <div className="friend-lock-overlay">
                      <div className="lock-content">
                        <div className="lock-icon">🔒</div>
                        <div className="lock-text">Premium Feature</div>
                        <div className="lock-subtext">Upgrade to access this friend</div>
                        <button 
                          className="btn btn-primary lock-upgrade-btn"
                          onClick={() => {
                            setPremiumModalReason('friend_access');
                            setShowPremiumModal(true);
                          }}
                        >
                          Upgrade Now
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              );
            })
          )}
        </div>
      )}

      {activeTab === 'appointments' && (
        <div className="appointments-list">
          {followUpReminders.length > 0 && (
            <div className="appointments-section">
              <h2 className="appointments-section-title">
                🔔 Follow-up Reminders ({followUpReminders.filter(reminder => reminder.status === 'pending').length} pending)
              </h2>
              <div className="appointments-grid">
                {followUpReminders
                  .slice()
                  .sort((a, b) => new Date(`${a.reminderDate || '1970-01-01'}T${a.reminderTime || '00:00'}`) - new Date(`${b.reminderDate || '1970-01-01'}T${b.reminderTime || '00:00'}`))
                  .map(reminder => {
                    const reminderDate = new Date(`${reminder.reminderDate || ''}T${reminder.reminderTime || '09:00'}`);
                    const isDue = !isNaN(reminderDate.getTime()) && reminderDate <= new Date();
                    return (
                      <div key={reminder.id} className="appointment-card">
                        <div className="appointment-header">
                          <div className="appointment-info">
                            <h3>Follow up with {reminder.friendName}</h3>
                            <p className="friend-name">
                              {isNaN(reminderDate.getTime()) ? 'Date not set' : reminderDate.toLocaleString()}
                            </p>
                          </div>
                          <div className="appointment-status">
                            <div className={`status-badge ${reminder.status || 'scheduled'}`}>
                              {reminder.status === 'completed' ? 'Completed' :
                               reminder.status === 'dismissed' ? 'Dismissed' :
                               isDue ? 'Due' : 'Pending'}
                            </div>
                          </div>
                        </div>

                        <div className="appointment-details">
                          {reminder.conversationSummary && (
                            <div className="detail-item">
                              <span className="detail-label">📝 Notes:</span>
                              <span className="detail-value">{reminder.conversationSummary}</span>
                            </div>
                          )}
                          <div className="detail-item">
                            <span className="detail-label">❓ Ask:</span>
                            <span className="detail-value">{reminder.followUpQuestion}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">📧 Email:</span>
                            <span className="detail-value">
                              {reminder.sendEmailReminder ? (reminder.emailSent ? 'Sent' : 'Scheduled') : 'Off'}
                            </span>
                          </div>
                        </div>

                        {reminder.status === 'pending' && (
                          <div className="appointment-actions">
                            <button
                              className="action-btn complete"
                              onClick={() => updateFollowUpReminderStatus(reminder.id, 'completed')}
                            >
                              ✅ Mark Done
                            </button>
                            <button
                              className="action-btn cancel"
                              onClick={() => updateFollowUpReminderStatus(reminder.id, 'dismissed')}
                            >
                              ❌ Dismiss
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {appointments.length === 0 && followUpReminders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📅</div>
              <h3>{t('noAppointmentsScheduled')}</h3>
              <p>Schedule your first appointment to stay connected with friends!</p>
            </div>
          ) : (
            (() => {
              // Separate appointments into upcoming and past
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              
              const upcomingAppointments = [];
              const pastAppointments = [];
              
              appointments.forEach(appointment => {
                // Handle date parsing - support both date strings and timestamp objects
                let appointmentDate;
                if (appointment.date) {
                  if (typeof appointment.date === 'string') {
                    // If time is provided, combine date and time
                    if (appointment.time) {
                      appointmentDate = new Date(`${appointment.date}T${appointment.time}`);
                    } else {
                      appointmentDate = new Date(appointment.date);
                    }
                  } else if (appointment.date.toDate) {
                    // Firestore Timestamp object
                    appointmentDate = appointment.date.toDate();
                  } else {
                    appointmentDate = new Date(appointment.date);
                  }
                } else {
                  appointmentDate = new Date();
                }
                
                // Check if date is valid
                const isValidDate = !isNaN(appointmentDate.getTime());
                const appointmentDateOnly = new Date(appointmentDate);
                appointmentDateOnly.setHours(0, 0, 0, 0);
                const isUpcoming = isValidDate && appointmentDateOnly >= now && appointment.status !== 'completed' && appointment.status !== 'cancelled';
                
                const appointmentWithDate = {
                  ...appointment,
                  parsedDate: appointmentDate,
                  isValidDate,
                  isUpcoming
                };
                
                if (isUpcoming) {
                  upcomingAppointments.push(appointmentWithDate);
                } else {
                  pastAppointments.push(appointmentWithDate);
                }
              });
              
              // Sort upcoming by date ascending, past by date descending
              upcomingAppointments.sort((a, b) => a.parsedDate - b.parsedDate);
              pastAppointments.sort((a, b) => b.parsedDate - a.parsedDate);
              
              const renderAppointmentCard = (appointment) => {
                const appointmentDate = appointment.parsedDate;
                const isValidDate = appointment.isValidDate;
                const isUpcoming = appointment.isUpcoming;
                
                // Format date for display
                const formattedDate = isValidDate 
                  ? appointmentDate.toLocaleDateString('en-US', { 
                      weekday: 'short',
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })
                  : appointment.date || 'Date not set';
                
                // Format time for display
                const formattedTime = appointment.time ? (() => {
                  const [hours, minutes] = appointment.time.split(':');
                  const hour = parseInt(hours, 10);
                  const ampm = hour >= 12 ? 'PM' : 'AM';
                  const displayHour = hour % 12 || 12;
                  return `${displayHour}:${minutes} ${ampm}`;
                })() : '';
                
                return (
                  <div key={appointment.id} className="appointment-card">
                    <div className="appointment-header">
                      <div className="appointment-info">
                        <h3>{appointment.title || 'Untitled Appointment'}</h3>
                        <p className="friend-name">with {appointment.friendName || 'Unknown Friend'}</p>
                      </div>
                      <div className="appointment-status">
                        <div className={`status-badge ${appointment.status || 'scheduled'}`}>
                          {appointment.status === 'scheduled' ? t('scheduled') :
                           appointment.status === 'completed' ? t('completed') :
                           appointment.status === 'cancelled' ? t('cancelled') : t('scheduled')}
                        </div>
                      </div>
                    </div>

                    <div className="appointment-details">
                      <div className="detail-item">
                        <span className="detail-label">📅 Date:</span>
                        <span className="detail-value">
                          {formattedDate}
                        </span>
                      </div>
                      {formattedTime && (
                        <div className="detail-item">
                          <span className="detail-label">🕐 Time:</span>
                          <span className="detail-value">{formattedTime}</span>
                        </div>
                      )}
                      {appointment.location && (
                        <div className="detail-item">
                          <span className="detail-label">📍 Location:</span>
                          <span className="detail-value">{appointment.location}</span>
                        </div>
                      )}
                      <div className="detail-item">
                        <span className="detail-label">🏷️ Type:</span>
                        <span className="detail-value">
                          {appointment.type?.replace(/\b\w/g, l => l.toUpperCase()) || 'Meeting'}
                        </span>
                      </div>
                    </div>

                    {appointment.notes && (
                      <div className="appointment-notes">
                        <p>"{appointment.notes}"</p>
                      </div>
                    )}

                    <div className="appointment-actions">
                      {appointment.status === 'scheduled' && isUpcoming && (
                        <>
                          <button 
                            className="action-btn complete"
                            onClick={() => updateAppointmentStatus(appointment.id, 'completed')}
                          >
                            ✅ Mark Complete
                          </button>
                          <button 
                            className="action-btn cancel"
                            onClick={() => updateAppointmentStatus(appointment.id, 'cancelled')}
                          >
                            ❌ Cancel
                          </button>
                        </>
                      )}
                      <button 
                        className="action-btn delete"
                        onClick={() => deleteAppointment(appointment.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                );
              };
              
              return (
                <>
                  {upcomingAppointments.length > 0 && (
                    <div className="appointments-section">
                      <h2 className="appointments-section-title">
                        📅 Upcoming Appointments ({upcomingAppointments.length})
                      </h2>
                      <div className="appointments-grid">
                        {upcomingAppointments.map(renderAppointmentCard)}
                      </div>
                    </div>
                  )}
                  
                  {pastAppointments.length > 0 && (
                    <div className="appointments-section">
                      <h2 className="appointments-section-title">
                        📋 Past Appointments ({pastAppointments.length})
                      </h2>
                      <div className="appointments-grid">
                        {pastAppointments.map(renderAppointmentCard)}
                      </div>
                    </div>
                  )}
                </>
              );
            })()
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="history-list">
          {contactHistory.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <h3>{t('noContactHistoryYet')}</h3>
              <p>{t('startReachingOut')}</p>
            </div>
          ) : (
            contactHistory.map(contact => {
              const contactDate = new Date(contact.date);
              const typeIcons = {
                'call': '📞',
                'email': '📧',
                'text': '💬',
                'meeting': '🤝',
                'video': '📹',
                'other': '💭'
              };
              
              return (
                <div key={contact.id} className="history-card">
                  <div className="history-header">
                    <div className="history-info">
                      <div className="contact-icon">
                        {typeIcons[contact.type] || '💭'}
                      </div>
                      <div className="contact-details">
                        <h3>{contact.friendName}</h3>
                        <p className="contact-type">
                          {getContactTypeLabel(contact.type)}
                        </p>
                      </div>
                    </div>
                    <div className="contact-date">
                      {contactDate.toLocaleDateString()} {t('at')} {contactDate.toLocaleTimeString()}
                    </div>
                  </div>

                  {(contact.notes || contact.duration) && (
                    <div className="contact-notes">
                      <p>"{contact.notes || contact.duration}"</p>
                    </div>
                  )}

                  <div className="contact-actions">
                    <button 
                      className="action-btn delete"
                      onClick={() => deleteContactRecord(contact.id)}
                    >
                      🗑️ {t('delete')}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Journey Assignment Modal */}
      {showJourneyModal && selectedFriendForJourney && (
        <div className="add-friend-modal">
          <div className="modal-content">
            <h3>{t('journeys')}</h3>
            
            {/* Existing Journeys */}
            {selectedFriendForJourney.journeys && selectedFriendForJourney.journeys.length > 0 && (
              <div className="existing-journeys">
                <h4>{t('activeJourneys')}</h4>
                {selectedFriendForJourney.journeys.map((journey, index) => {
                  // Use correct totals for each week (don't rely on stored totals which may be outdated)
                  const correctTotals = {
                    week1: 6,
                    week2: 4,
                    week3: 4,
                    week4: 3
                  };
                  
                  const progress = Object.values(journey.progress).reduce((sum, week) => sum + week.completed, 0);
                  // Calculate total using correct week totals instead of stored totals
                  const total = Object.keys(correctTotals).reduce((sum, weekKey) => {
                    return sum + (correctTotals[weekKey] || 0);
                  }, 0);
                  const percentage = Math.round((progress / total) * 100);
                  
                  return (
                    <div key={journey.id} className="journey-item">
                      <div className="journey-info">
                        <h5>{journey.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</h5>
                        <p>{t('started')} {new Date(journey.startDate).toLocaleDateString()}</p>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <p>{t('activitiesCompletedCount', { completed: progress, total: total, percentage: percentage })}</p>
                      </div>
                      <button 
                        className="btn btn-primary"
                        onClick={() => {
                          if (onStartJourney) {
                            onStartJourney(selectedFriendForJourney.id, journey.id);
                          }
                          setShowJourneyModal(false);
                          setSelectedFriendForJourney(null);
                        }}
                      >
                        Continue Journey
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Start New Journey */}
            <div className="journey-options">
              <h4>{t('startNewJourneyModal')}</h4>
              <div className="journey-option">
                <h5>🛤️ {t('reconnectAndDeepen')}</h5>
                <p>{t('journeyDescription')}</p>
                <button 
                  className="btn btn-secondary"
                  onClick={async () => {
                    const newJourneyId = await assignJourney(
                      selectedFriendForJourney.id,
                      'deepening-relationships'
                    );
                    if (newJourneyId && onStartJourney) {
                      onStartJourney(selectedFriendForJourney.id, newJourneyId);
                    }
                    setShowJourneyModal(false);
                    setSelectedFriendForJourney(null);
                  }}
                >
                  {t('startNewJourneyModal')}
                </button>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => {
                  setShowJourneyModal(false);
                  setSelectedFriendForJourney(null);
                }}
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Friend Details Modal */}
      {showFriendDetails && selectedFriendForDetails && (
        <div className="add-friend-modal">
          <div className="modal-content friend-details-modal">
            <h3>{selectedFriendForDetails.name} - {t('interactionHistory')}</h3>
            
            <div className="friend-details-header">
              <div className="friend-summary">
                <div className="friend-avatar-large">
                  {getRelationshipIcon(selectedFriendForDetails.relationship)}
                </div>
                <div className="friend-info-large">
                  <h4>{selectedFriendForDetails.name}</h4>
                  <p className="relationship-type">
                    {getFriendshipTypeName(selectedFriendForDetails.relationship)}
                  </p>
                  <div className="contact-status">
                    <span 
                      className="status-indicator"
                      style={{ backgroundColor: getContactStatus(selectedFriendForDetails, t).color }}
                    >
                      {getContactStatus(selectedFriendForDetails, t).message}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Tracking Section */}
            <div className="progress-tracking-section">
              {(() => {
                // Define friendInteractions here so it can be used by the cadence tracker
                console.log('Filtering friendInteractions for friend:', selectedFriendForDetails.id);
                console.log('Total contactHistory length:', contactHistory.length);
                console.log('ContactHistory data:', contactHistory);
                
                const friendInteractions = contactHistory.filter(c => {
                  // Ensure both IDs are strings for comparison
                  const contactFriendId = String(c.friendId);
                  const selectedFriendId = String(selectedFriendForDetails.id);
                  const matches = contactFriendId === selectedFriendId;
                  console.log('Checking contact:', c.friendId, 'vs selected:', selectedFriendForDetails.id, 'matches:', matches);
                  return matches;
                });
                
                console.log('Filtered friendInteractions:', friendInteractions.length, 'interactions');
                console.log('FriendInteractions data:', friendInteractions);
                
                return (
                  <CadenceTracker
                    friendId={selectedFriendForDetails.id}
                    friendName={selectedFriendForDetails.name}
                    contactHistory={friendInteractions}
                    targetFrequency={selectedFriendForDetails.contactFrequency}
                    frequencyHistory={selectedFriendForDetails.frequencyHistory || []}
                  />
                );
              })()}
            </div>

            <div className="interactions-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4>{t('allInteractions')}</h4>
                <button 
                  className="btn btn-secondary"
                  onClick={reloadContactHistory}
                  style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                >
                  {t('reload')}
                </button>
              </div>
              {(() => {
                console.log('Friend details modal - selectedFriendForDetails:', selectedFriendForDetails);
                console.log('Friend details modal - contactHistory:', contactHistory);
                console.log('Friend details modal - appointments:', appointments);
                
                console.log('Filtering for friendId:', selectedFriendForDetails.id, 'type:', typeof selectedFriendForDetails.id);
                
                const friendInteractions = contactHistory.filter(c => {
                  // Ensure both IDs are strings for comparison
                  const contactFriendId = String(c.friendId);
                  const selectedFriendId = String(selectedFriendForDetails.id);
                  console.log('Checking contact:', c.friendId, 'type:', typeof c.friendId, 'matches:', contactFriendId === selectedFriendId);
                  return contactFriendId === selectedFriendId;
                });
                const friendAppointments = appointments.filter(a => {
                  // Ensure both IDs are strings for comparison
                  const appointmentFriendId = String(a.friendId);
                  const selectedFriendId = String(selectedFriendForDetails.id);
                  return appointmentFriendId === selectedFriendId;
                });
                
                console.log('Friend details modal - friendInteractions:', friendInteractions);
                console.log('Friend details modal - friendAppointments:', friendAppointments);
                
                // Combine and sort all interactions by date
                const allInteractions = [
                  ...friendInteractions.map(interaction => ({
                    ...interaction,
                    type: 'contact',
                    displayType: interaction.type,
                    date: interaction.date,
                    title: `${getContactTypeLabel(interaction.type)} ${t('with')} ${interaction.friendName}`,
                    details: interaction.notes || interaction.duration || ''
                  })),
                  ...friendAppointments.map(appointment => ({
                    ...appointment,
                    type: 'appointment',
                    displayType: appointment.type,
                    date: appointment.date,
                    title: appointment.title,
                    details: appointment.notes || '',
                    duration: appointment.time || '',
                    status: appointment.status
                  }))
                ].sort((a, b) => new Date(b.date) - new Date(a.date));

                if (allInteractions.length === 0) {
                  return (
                    <div className="empty-interactions">
                      <div className="empty-icon">📝</div>
                      <p>{t('noInteractionsRecorded')}</p>
                    </div>
                  );
                }

                return (
                  <div className="interactions-list">
                    {allInteractions.map((interaction, index) => {
                      const interactionDate = new Date(interaction.date);
                      const typeIcons = {
                        'call': '📞',
                        'email': '📧',
                        'text': '💬',
                        'meeting': '🤝',
                        'video': '📹',
                        'lunch': '🍽️',
                        'dinner': '🍽️',
                        'coffee': '☕',
                        'activity': '🎯',
                        'other': '💭'
                      };
                      
                      return (
                        <div key={`${interaction.type}-${interaction.id || index}`} className="interaction-item">
                          <div className="interaction-header">
                            <div className="interaction-icon">
                              {typeIcons[interaction.displayType] || '💭'}
                            </div>
                            <div className="interaction-info">
                              <h5>{interaction.title}</h5>
                              <div className="interaction-meta">
                                <span className="interaction-date">
                                  {interactionDate.toLocaleDateString()} {t('at')} {interactionDate.toLocaleTimeString()}
                                </span>
                                {interaction.status && (
                                  <span className={`interaction-status ${interaction.status}`}>
                                    {interaction.status === 'scheduled' ? t('scheduled') :
                                     interaction.status === 'completed' ? t('completed') : t('cancelled')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {interaction.details && (
                            <div className="interaction-notes">
                              <p>"{interaction.details}"</p>
                            </div>
                          )}
                          
                          {interaction.type === 'contact' && (
                            <div className="interaction-actions">
                              {findReflectionForInteraction(interaction) && (
                                <button 
                                  className="action-btn view-reflection"
                                  onClick={() => {
                                    setSelectedReflection(findReflectionForInteraction(interaction));
                                    setShowReflectionView(true);
                                  }}
                                  style={{ marginRight: '8px' }}
                                >
                                  📝 {t('viewReflection')}
                                </button>
                              )}
                              <button 
                                className="action-btn delete"
                                onClick={() => deleteContactRecord(interaction.id)}
                              >
                                🗑️ {t('delete')}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => {
                  setShowFriendDetails(false);
                  setSelectedFriendForDetails(null);
                  if (onFriendDetailsClosed) {
                    onFriendDetailsClosed();
                  }
                }}
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      {showContactModal && selectedFriendForContact && (
        <div className="add-friend-modal">
          <div className="modal-content">
            <h3>{t('logInteraction')} {t('with') || 'with'} {selectedFriendForContact.name}</h3>
            <form onSubmit={saveContactForm}>
              <div className="form-group">
                <label>{t('contactType')} *</label>
                <select
                  value={contactForm.type}
                  onChange={(e) => setContactForm({...contactForm, type: e.target.value})}
                  required
                >
                  <option value="call">{t('phoneCall')}</option>
                  <option value="email">{t('emailContact')}</option>
                  <option value="text">{t('textMessage')}</option>
                  <option value="video">{t('videoCall')}</option>
                  <option value="meeting">{t('inPersonMeeting')}</option>
                  <option value="other">{t('otherContact')}</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('date')} *</label>
                  <input
                    type="date"
                    value={contactForm.date}
                    onChange={(e) => setContactForm({...contactForm, date: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('time')} *</label>
                  <input
                    type="time"
                    value={contactForm.time}
                    onChange={(e) => setContactForm({...contactForm, time: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('notes')}</label>
                <textarea
                  value={contactForm.notes}
                  onChange={(e) => setContactForm({...contactForm, notes: e.target.value})}
                  placeholder={t('notesPlaceholderContact')}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={contactForm.createFollowUpReminder}
                    onChange={(e) => setContactForm({...contactForm, createFollowUpReminder: e.target.checked})}
                  />
                  <span>Create follow-up reminder</span>
                </label>
              </div>

              {contactForm.createFollowUpReminder && (
                <div className="appointment-planning">
                  <h4>Follow-up reminder</h4>

                  <div className="form-group">
                    <label>Important conversation notes</label>
                    <textarea
                      value={contactForm.conversationSummary}
                      onChange={(e) => setContactForm({...contactForm, conversationSummary: e.target.value})}
                      placeholder="What did you talk about that you want to remember?"
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>Follow-up question *</label>
                    <input
                      type="text"
                      value={contactForm.followUpQuestion}
                      onChange={(e) => setContactForm({...contactForm, followUpQuestion: e.target.value})}
                      placeholder="What do you want to ask next time?"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Reminder date *</label>
                      <input
                        type="date"
                        value={contactForm.followUpDate}
                        onChange={(e) => setContactForm({...contactForm, followUpDate: e.target.value})}
                      />
                    </div>

                    <div className="form-group">
                      <label>Reminder time</label>
                      <input
                        type="time"
                        value={contactForm.followUpTime}
                        onChange={(e) => setContactForm({...contactForm, followUpTime: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={contactForm.emailReminder}
                        onChange={(e) => setContactForm({...contactForm, emailReminder: e.target.checked})}
                      />
                      <span>Also send me an email reminder</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={contactForm.plannedAppointment}
                    onChange={(e) => setContactForm({...contactForm, plannedAppointment: e.target.checked})}
                  />
                  <span>{t('plannedAppointment')}</span>
                </label>
              </div>

              {contactForm.plannedAppointment && (
                <div className="appointment-planning">
                  <h4>{t('appointmentDetails')}</h4>
                  
                  <div className="form-group">
                    <label>{t('appointmentTitle')} *</label>
                    <input
                      type="text"
                      value={contactForm.appointmentTitle}
                      onChange={(e) => setContactForm({...contactForm, appointmentTitle: e.target.value})}
                      placeholder={t('appointmentTitlePlaceholder')}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('appointmentDate')} *</label>
                      <input
                        type="date"
                        value={contactForm.appointmentDate}
                        onChange={(e) => setContactForm({...contactForm, appointmentDate: e.target.value})}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('appointmentTime')} *</label>
                      <input
                        type="time"
                        value={contactForm.appointmentTime}
                        onChange={(e) => setContactForm({...contactForm, appointmentTime: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={contactForm.appointmentLocation}
                      onChange={(e) => setContactForm({...contactForm, appointmentLocation: e.target.value})}
                      placeholder="Where are you meeting?"
                    />
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  disabled={isSavingContact}
                  onClick={() => {
                    setShowContactModal(false);
                    setSelectedFriendForContact(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" disabled={isSavingContact}>
                  {isSavingContact ? 'Saving...' : 'Save Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Goals Modal */}
      {showGoalsModal && selectedFriendForGoals && (
        <div className="add-friend-modal">
          <div className="modal-content">
            <h3>{t('goalsFor')} {selectedFriendForGoals.name}</h3>
            
            {/* Existing Goals */}
            {selectedFriendForGoals.goals && selectedFriendForGoals.goals.length > 0 && (
              <div className="existing-goals">
                <h4>{t('currentGoals')}</h4>
                {selectedFriendForGoals.goals.map((goal) => (
                  <div key={goal.id} className="goal-item">
                    <div className="goal-header">
                      <div className="goal-header-content">
                        <h5>{goal.title}</h5>
                        <span className="goal-category-badge">
                          {getGoalCategoryLabel(goal.category)}
                        </span>
                      </div>
                      <button 
                        className="goal-delete-btn"
                        onClick={() => deleteGoal(selectedFriendForGoals.id, goal.id)}
                      >
                        🗑️
                      </button>
                    </div>
                    {goal.description && (
                      <p>{goal.description}</p>
                    )}
                    {goal.targetDate && (
                      <p className="goal-target-date">
                        {t('targetLabel')} {new Date(goal.targetDate).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add New Goal Form */}
            <form onSubmit={addGoal}>
              <div className="form-group">
                <label>{t('goalTitleLabel')}</label>
                <input
                  type="text"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                  placeholder={t('goalTitlePlaceholder')}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('goalDescriptionLabel')}</label>
                <textarea
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                  placeholder={t('goalDescriptionPlaceholder')}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('goalCategoryLabel')}</label>
                  <select
                    value={newGoal.category}
                    onChange={(e) => setNewGoal({...newGoal, category: e.target.value})}
                    required
                  >
                    <option value="">{t('selectCategory')}</option>
                    <option value="connection">{t('goalCategoryConnection')}</option>
                    <option value="communication">{t('goalCategoryCommunication')}</option>
                    <option value="fun">{t('goalCategoryFun')}</option>
                    <option value="support">{t('goalCategorySupport')}</option>
                    <option value="growth">{t('goalCategoryGrowth')}</option>
                    <option value="other">{t('goalCategoryOther')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('targetDateOptional')}</label>
                  <input
                    type="date"
                    value={newGoal.targetDate}
                    onChange={(e) => setNewGoal({...newGoal, targetDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowGoalsModal(false);
                    setSelectedFriendForGoals(null);
                    setNewGoal({
                      title: '',
                      description: '',
                      category: '',
                      targetDate: ''
                    });
                  }}
                >
                  {t('close')}
                </button>
                <button type="submit">{t('addGoal')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Friendship Type Modal */}
      {showFriendshipTypeOnLoad && selectedFriendForType && (
        <div className="add-friend-modal">
          <div className="modal-content">
            <h3>{t('selectFriendshipTypeModal')} {t('for') || 'for'} {selectedFriendForType.name}</h3>
            
            <div className="friendship-types-grid">
              {Object.entries(FRIENDSHIP_TYPES).map(([id, type]) => {
                const getFriendshipTypeName = (typeId) => {
                  const nameMap = {
                    'acquaintance': t('friendshipTypeAcquaintance'),
                    'activity-based': t('friendshipTypeActivityBased'),
                    'situational': t('friendshipTypeSituational'),
                    'close': t('friendshipTypeClose'),
                    'lifelong': t('friendshipTypeLifelong'),
                    'mentor-mentee': t('friendshipTypeMentorMentee'),
                    'workplace': t('friendshipTypeWorkplace'),
                    'chosen-family': t('friendshipTypeChosenFamily'),
                    'digital-first': t('friendshipTypeDigitalFirst')
                  };
                  return nameMap[typeId] || type.name;
                };

                const getFriendshipTypeDescription = (typeId) => {
                  const descMap = {
                    'acquaintance': t('friendshipTypeAcquaintanceDesc'),
                    'activity-based': t('friendshipTypeActivityBasedDesc'),
                    'situational': t('friendshipTypeSituationalDesc'),
                    'close': t('friendshipTypeCloseDesc'),
                    'lifelong': t('friendshipTypeLifelongDesc'),
                    'mentor-mentee': t('friendshipTypeMentorMenteeDesc'),
                    'workplace': t('friendshipTypeWorkplaceDesc'),
                    'chosen-family': t('friendshipTypeChosenFamilyDesc'),
                    'digital-first': t('friendshipTypeDigitalFirstDesc')
                  };
                  return descMap[typeId] || type.description;
                };

                return (
                  <div
                    key={id}
                    className={`friendship-type-card ${selectedFriendForType.relationship === id ? 'selected' : ''}`}
                    onClick={async () => {
                      try {
                        const friendRef = doc(db, 'friends', selectedFriendForType.id);
                        await updateDoc(friendRef, {
                          relationship: id,
                          updatedAt: new Date().toISOString()
                        });
                        
                        // Update local state
                        setFriends(friends.map(f => 
                          f.id === selectedFriendForType.id 
                            ? { ...f, relationship: id }
                            : f
                        ));
                        
                        if (onFriendshipTypeClosed) {
                          onFriendshipTypeClosed();
                        }
                      } catch (error) {
                        console.error('Error updating friendship type:', error);
                      }
                    }}
                  >
                    <div>
                      {type.icon}
                    </div>
                    <h4>
                      {getFriendshipTypeName(id)}
                    </h4>
                    <p>
                      {getFriendshipTypeDescription(id)}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => onFriendshipTypeClosed && onFriendshipTypeClosed()}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal 
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        reason={premiumModalReason}
      />

      {/* Reflection Prompt */}
      {console.log('Rendering ReflectionPrompt with:', { showReflectionPrompt, reflectionData })}
      {reflectionData && (
        <ReflectionPrompt
          isOpen={showReflectionPrompt}
          onClose={() => {
            setShowReflectionPrompt(false);
            setReflectionData(null);
          }}
          friendName={reflectionData.friendName}
          contactType={reflectionData.contactType}
          onSaveReflection={handleSaveReflection}
        />
      )}

      {/* Reflection View Modal */}
      {showReflectionView && selectedReflection && (
        <div className="add-friend-modal">
          <div className="modal-content">
            <h3>{t('reflectionEvaluation')}</h3>
            <div className="reflection-view-content">
              <div className="reflection-section">
                <div className="reflection-label">{t('howWasConversation')}</div>
                <div className="reflection-value">
                  {selectedReflection.mood ? (
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      background: selectedReflection.mood === 'excellent' ? '#10B98120' :
                                   selectedReflection.mood === 'good' ? '#22C55E20' :
                                   selectedReflection.mood === 'okay' ? '#F59E0B20' : '#EF444420',
                      color: selectedReflection.mood === 'excellent' ? '#10B981' :
                             selectedReflection.mood === 'good' ? '#22C55E' :
                             selectedReflection.mood === 'okay' ? '#F59E0B' : '#EF4444',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {selectedReflection.mood === 'excellent' ? '😊 Excellent' :
                       selectedReflection.mood === 'good' ? '🙂 Good' :
                       selectedReflection.mood === 'okay' ? '😐 Okay' :
                       selectedReflection.mood === 'difficult' ? '😔 Difficult' : selectedReflection.mood}
                    </span>
                  ) : t('notProvided')}
                </div>
              </div>

              <div className="reflection-section">
                <div className="reflection-label">{t('topicsDiscussed')}</div>
                <div className="reflection-value">{formatReflectionTopics(selectedReflection.topics)}</div>
              </div>

              {selectedReflection.highlights && (
                <div className="reflection-section">
                  <div className="reflection-label">{t('highlights')}</div>
                  <div className="reflection-value">{selectedReflection.highlights}</div>
                </div>
              )}

              {selectedReflection.nextSteps && (
                <div className="reflection-section">
                  <div className="reflection-label">{t('nextSteps')}</div>
                  <div className="reflection-value">{selectedReflection.nextSteps}</div>
                </div>
              )}

              {selectedReflection.overall && (
                <div className="reflection-section">
                  <div className="reflection-label">{t('overallReflection')}</div>
                  <div className="reflection-value">{selectedReflection.overall}</div>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => {
                  setShowReflectionView(false);
                  setSelectedReflection(null);
                }}
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendshipTracker;